var kind               = require("enyo/kind"),
    ComponentTaskQueue = require("enyo-perf/ComponentTaskQueue");

/**
 * [AbsoluteShowingQueueSupport]{@link enyo-perf.AbsoluteShowingQueueSupport}
 * helps defer tasks if the control is not currently `absoluteShowing`.
 *
 * The main idea is, if the results of these tasks will be invisible as long as
 * the control remains invisible, their execution may as well be delayed until
 * the control becomes visible again.
 *
 * This mixin creates methods to add/remove tasks to a dedicated
 * [ComponentTaskQueue]{@link enyo-perf.ComponentTaskQueue} and will flush the
 * queue automatically once the control becomes `absoluteShowing`.
 *
 * @mixin enyo-perf.AbsoluteShowingQueueSupport
 * @public
 */
module.exports = {

    /**
     * @private
     */
    name: "enyo-perf.AbsoluteShowingQueueSupport",

    /**
     * Sets up a [queue]{@link enyo-perf.ComponentTaskQueue} for tasks that get
     * deferred while this control is not `absoluteShowing`.
     *
     * @memberof enyo-perf.AbsoluteShowingQueueSupport
     * @method
     * @private
     */
    initComponents: kind.inherit(function(sup) {
        return function() {
            this.createComponent({
                name:  "absoluteShowingQueue",
                kind:  ComponentTaskQueue,
                guard: this.bindSafely(function() {
                    return this.absoluteShowing;
                })
            });
            sup.apply(this, arguments);
        };
    }),

    /**
     * Flush [absoluteShowingQueue]{@link enyo-perf.ComponentTaskQueue} when
     * control becomes `absoluteShowing`.
     *
     * @memberof enyo-perf.AbsoluteShowingQueueSupport
     * @method
     * @private
     */
    absoluteShowingChanged: kind.inherit(function(sup) {
        return function(wasAbsoluteShowing) {
            sup.apply(this, arguments);

            if (!wasAbsoluteShowing && this.absoluteShowing) {
                this.flushAbsoluteShowingQueue();
            }
        };
    }),

    /**
     * Add a deferred task to the [absoluteShowingQueue]{@link
     * enyo-perf.ComponentTaskQueue}. The deferred tasks will get executed once
     * we are `absoluteShowing`.
     *
     * @param    {string}  method  The name of the deferred method
     * @param    {Array=}  args    The arguments to pass to the deferred method.
     * @memberof enyo-perf.AbsoluteShowingQueueSupport
     * @public
     */
    addToAbsoluteShowingQueue: function(method, args) {
        if (this.$.absoluteShowingQueue) {
            this.$.absoluteShowingQueue.addTask(method, args);
        }
    },

    /**
     * Removes a deferred task from the [absoluteShowingQueue]{@link
     * enyo-perf.ComponentTaskQueue}.
     *
     * @param    {string}  method  The name of the deferred method
     * @memberof enyo-perf.AbsoluteShowingQueueSupport
     * @public
     */
    removeFromAbsoluteShowingQueue: function(method) {
        if (this.$.absoluteShowingQueue) {
            this.$.absoluteShowingQueue.removeTask(method);
        }
    },

    /**
     * Flushes the [absoluteShowingQueue]{@link enyo-perf.ComponentTaskQueue}.
     *
     * @memberof enyo-perf.AbsoluteShowingQueueSupport
     * @public
     */
    flushAbsoluteShowingQueue: function() {
        if (this.$.absoluteShowingQueue) {
            this.$.absoluteShowingQueue.flush();
        }
    },

    /**
     * Clears the [absoluteShowingQueue]{@link enyo-perf.ComponentTaskQueue}.
     *
     * @memberof enyo-perf.AbsoluteShowingQueueSupport
     * @public
     */
    clearAbsoluteShowingQueue: function() {
        if (this.$.absoluteShowingQueue) {
            this.$.absoluteShowingQueue.clear();
        }
    },

    /**
     * @memberof enyo-perf.AbsoluteShowingQueueSupport
     * @method
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            this.clearAbsoluteShowingQueue();
            sup.apply(this, arguments);
        };
    })

};
